import { type Blob, type Finding } from '@consigli/types';
import { ChevronDown, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { prefersReducedMotion } from '@/util/prefers-reduced-motion';

import { type AddContextMenuKind } from '../types';
import {
  AddContextDocumentChip,
  AddContextFindingChip,
  AddContextMoreChip,
} from './add-context-attachment-chip';
import { attachedContextKind } from './finding-context-kind';

const ICON_STROKE_WIDTH = 1.75;

/**
 * One chip row, which is what the collapsed strip is the height of. A constant
 * rather than a measurement: the collapsed layout has not been rendered yet
 * when the collapse starts, and this is the chip's own height
 * (`--ds-space-8`), not something that varies with content.
 *
 * The expanded height has no constant here on purpose. The design caps the
 * stack at seven rows, `add-context-strip-stacked` holds that cap, and the
 * animation reads the strip's laid-out height - so the number lives in one
 * place instead of having to agree with a copy in CSS.
 */
const ROW_HEIGHT = 32;

const HEADING_KEYS: Record<AddContextMenuKind, string> = {
  document: 'prop-chat.add-context-attachments-heading-document',
  finding: 'prop-chat.add-context-attachments-heading-finding',
  milestone: 'prop-chat.add-context-attachments-heading-milestone',
  'response-requirement': 'prop-chat.add-context-attachments-heading-response-requirement',
};

const TRANSITION_MS = 200;

/**
 * `transitionend` does not always arrive - no transition runs when the two
 * heights happen to match, and none runs at all in a test environment - and
 * without it the strip would stay stacked with its height pinned. Same guard,
 * and the same reason, as `agent-chat-drawer.tsx`.
 */
const TRANSITION_FALLBACK_MS = TRANSITION_MS + 50;

interface AddContextAttachmentsProps {
  /**
   * How many chips the collapsed row shows before the rest become the count.
   * One in the drawer, three in fullscreen, where the input is a 640px column
   * with room to split.
   */
  collapsedChipCount: number;
  documents: Blob[];
  findings: Finding[];
  /** Detaches every document and finding at once. */
  onClearAll: () => void;
  onRemoveDocument: (document: Blob) => void;
  onRemoveFinding: (finding: Finding) => void;
}

/*
 * What is attached to the next question, above the input.
 *
 * Two states from the design. Collapsed shows the first chip and a "+N" for
 * the rest; expanded stacks them all full-width. The toggle only appears from
 * two chips up, because with one there is no rest to show.
 *
 * From two chips up the strip also sits in a tray headed by the count and a
 * "Clear all". The tray's edge is what tells the user "all" means every chip in
 * it, hidden ones included, and not the chip nearest the button. One chip gets
 * no tray: its own remove control already clears everything.
 *
 * The switch is animated by transitioning the wrapper's height between the two
 * layouts' own heights. Two things make that work:
 *
 *  - `isStacked` lags `isExpanded` through a collapse. Flipping back to the row
 *    at the start would drop the extra chips instantly and leave the animation
 *    shrinking empty space.
 *  - the height is explicit only while animating, and released to `auto` when
 *    it lands, so adding or removing a chip afterwards just reflows.
 */
export const AddContextAttachments: FC<AddContextAttachmentsProps> = ({
  collapsedChipCount,
  documents,
  findings,
  onClearAll,
  onRemoveDocument,
  onRemoveFinding,
}) => {
  const { t } = useTranslation();
  const headingId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isStacked, setIsStacked] = useState(false);
  const [height, setHeight] = useState<number | null>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const fallbackRef = useRef<number | null>(null);
  // Read by `settle`, which would otherwise have to be rebuilt - and its
  // pending timer re-registered - every time the state flips.
  const isExpandedRef = useRef(isExpanded);
  isExpandedRef.current = isExpanded;

  const total = findings.length + documents.length;
  // Something to reveal: more attached than the collapsed row can show.
  const hasMore = total > collapsedChipCount;
  const hasTray = total > 1;

  const clearPending = useCallback(() => {
    if (fallbackRef.current !== null) {
      window.clearTimeout(fallbackRef.current);
      fallbackRef.current = null;
    }
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  }, []);

  const settle = useCallback(() => {
    clearPending();
    setIsStacked(isExpandedRef.current);
    // Back to auto, so the strip follows its own content from here.
    setHeight(null);
  }, [clearPending]);

  useEffect(() => clearPending, [clearPending]);

  const toggle = useCallback(() => {
    const next = !isExpanded;

    if (prefersReducedMotion()) {
      clearPending();
      setIsExpanded(next);
      setIsStacked(next);
      setHeight(null);
      return;
    }

    const current = stripRef.current?.offsetHeight ?? ROW_HEIGHT;

    setIsExpanded(next);
    isExpandedRef.current = next;

    clearPending();
    fallbackRef.current = window.setTimeout(settle, TRANSITION_FALLBACK_MS);

    setHeight(current);
    // A frame between the two heights, or the browser coalesces them into one
    // style change and there is nothing to transition from. Expanding also
    // needs the stacked layout in place before the target can be measured.
    if (next) setIsStacked(true);
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      setHeight(next ? (stripRef.current?.offsetHeight ?? null) : ROW_HEIGHT);
    });
  }, [clearPending, isExpanded, settle]);

  if (total === 0) return null;

  /*
   * Collapsed fills its allowance from the findings first, then tops up from
   * the documents, so the chips are the front of one list rather than one from
   * each.
   *
   * The chips share the row and are capped at the design's 247px rather than
   * fixed at it: three of them in fullscreen come out around 180px each, and a
   * drawer dragged narrower than one chip plus its count and chevron shrinks
   * the chip instead of overflowing.
   */
  const visibleFindings = isStacked ? findings : findings.slice(0, collapsedChipCount);
  const visibleDocuments = isStacked
    ? documents
    : documents.slice(0, Math.max(0, collapsedChipCount - visibleFindings.length));
  const hiddenCount = isStacked ? 0 : total - (visibleFindings.length + visibleDocuments.length);
  const chipClass = isStacked ? 'max-w-full' : 'min-w-0 max-w-[247px] flex-1';

  const strip = (
    <div
      /*
       * `w-full` in the tray, never `flex-1`: the tray is a column, where a
       * zero flex-basis would override the explicit height the animation sets.
       */
      className={`${hasTray ? 'w-full' : 'min-w-0 flex-1'} overflow-hidden transition-[height] duration-200 ease-out motion-reduce:transition-none`}
      onTransitionEnd={(event) => {
        if (event.propertyName === 'height') settle();
      }}
      style={height === null ? undefined : { height }}
    >
      <div
        ref={stripRef}
        className={
          isStacked
            ? 'add-context-strip-stacked flex flex-col gap-[var(--ds-space-2)] overscroll-contain'
            : 'flex items-start gap-[var(--ds-space-2)]'
        }
      >
        {visibleFindings.map((finding) => (
          <AddContextFindingChip
            key={`finding-${finding.id}`}
            className={chipClass}
            finding={finding}
            onRemove={() => onRemoveFinding(finding)}
          />
        ))}
        {visibleDocuments.map((document) => (
          <AddContextDocumentChip
            key={`document-${document.id}`}
            className={chipClass}
            document={document}
            onRemove={() => onRemoveDocument(document)}
          />
        ))}
        {hiddenCount > 0 && (
          /*
           * `toggle` rather than an expand-only handler: the count is only
           * ever rendered from the row, since a collapse keeps the stack up
           * until it lands, so the toggle it reaches for is always the one
           * that opens.
           */
          <AddContextMoreChip count={hiddenCount} onExpand={toggle} />
        )}
      </div>
    </div>
  );

  /*
   * Marked for the composer's blur handler, the same way the add-context
   * trigger and menu are: reaching for the count now moves focus out of the
   * textarea, and without this a composer the user had expanded would snap
   * back to one line behind the stack it was opening. On the whole strip
   * rather than the count alone - the count and the chevron are two halves of
   * one disclosure, and removing a chip is no more "leaving the composer"
   * than expanding is.
   */
  if (!hasTray) {
    return (
      <div data-add-context="" className="flex w-full shrink-0">
        {strip}
      </div>
    );
  }

  return (
    <div
      aria-labelledby={headingId}
      className="flex w-full shrink-0 flex-col gap-[var(--ds-space-2)] rounded-[var(--ds-radius-10)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-secondary)] p-[var(--ds-space-2)]"
      data-add-context=""
      role="group"
    >
      <div className="flex items-center gap-[var(--ds-space-1)] pl-[var(--ds-space-1)]">
        <span
          className="min-w-0 flex-1 truncate text-[length:var(--ds-typography-paragraph-mini-font-size)] leading-[var(--ds-typography-paragraph-mini-line-height)] tabular-nums text-[var(--ds-color-text-secondary)]"
          id={headingId}
        >
          {t(HEADING_KEYS[attachedContextKind({ documents, findings }) ?? 'document'], {
            count: total,
          })}
        </span>
        {/*
         * Named by its visible text alone, inside a group named by the
         * heading, so a screen reader hears "Clear all" with the count beside
         * it and the spoken name still matches what a voice user reads aloud.
         */}
        <button
          className="add-context-clear-all flex h-[var(--ds-space-8)] shrink-0 cursor-pointer items-center gap-[var(--ds-space-1)] rounded-[var(--ds-radius-8)] px-[var(--ds-space-2)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] leading-[var(--ds-typography-paragraph-13-regular-line-height)] text-[var(--ds-color-text-secondary)] hover:bg-[var(--ds-color-bg-light-negative)] hover:text-[var(--ds-color-text-negative)]"
          onClick={onClearAll}
          type="button"
        >
          <Trash2
            aria-hidden="true"
            strokeWidth={ICON_STROKE_WIDTH}
            className="size-[var(--ds-space-4)]"
          />
          {t('prop-chat.add-context-clear-all')}
        </button>
        {hasMore && (
          <button
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? t('prop-chat.add-context-collapse-attachments')
                : t('prop-chat.add-context-expand-attachments', { count: total })
            }
            className="add-context-chip-toggle flex size-[var(--ds-space-8)] shrink-0 cursor-pointer items-center justify-center rounded-[var(--ds-radius-8)] border border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] text-[var(--ds-color-bg-icon-primary)]"
            onClick={toggle}
            type="button"
          >
            {/*
             * One chevron rotated rather than the design's two glyphs: the
             * result is the same arrow, and it turns into place instead of
             * swapping. The design system rotates its own Select indicator the
             * same way.
             */}
            <ChevronDown
              aria-hidden="true"
              strokeWidth={ICON_STROKE_WIDTH}
              className={`size-[var(--ds-space-4)] transition-transform duration-150 ease-out motion-reduce:transition-none ${
                isExpanded ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </button>
        )}
      </div>
      {strip}
    </div>
  );
};
